/**
 * 교육 과정 · 강의
 *  - 과정: 추가/수정/삭제(기록 없을 때)/폐지·다시 운영. BT 는 삭제 불가
 *  - 강의 개설형: 강의 개설 → 출석·이수 등록 (선택 / 엑셀 / QR 출석 코드)
 *  - 연도별 수료 체크형 (CC101 매년, CC201 2년마다): 연도 행 + 수료 체크
 */
import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, InputAdornment, MenuItem, Switch, TextField, Typography,
} from '@mui/material';
import { Delete, Edit, Search as SearchIcon } from '@mui/icons-material';
import { api } from '../api';
import { C, ymd } from '../common';
import { Card, Empty, ToneChip } from './ui';
import type { RosterTeacher } from './ChurchTeachersTab';

type Course = {
  _id: string; code: string; name: string; description?: string; category: string; criteria?: string; prerequisites: string[];
  issuesCertificate: boolean; mode: 'session' | 'yearly'; cycleYears: 1 | 2; years: number[]; status: 'active' | 'retired'; system: boolean;
  recordCount: number; sessionCount: number; yearCounts: Array<{ year: number; count: number }>;
};
type Session = { _id: string; title: string; date?: string; place?: string; recruiting: boolean; recordCount: number };
type Rec = { _id: string; teacherName: string; result: string; source: string; personKey: string; teacherId?: string };

const BASE_CATEGORIES = ['기본', '심화', 'CC', '특강', '기타'];

/** 엑셀 → [{name, phone}] (이름 / 휴대폰 열을 찾아서) */
const readExcelRows = async (file: File) => {
  const wb = XLSX.read(await file.arrayBuffer());
  const rows: any[][] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false });
  const header = (rows[0] || []).map((h) => String(h || ''));
  let ni = header.findIndex((h) => /이름|성명|name/i.test(h));
  let pi = header.findIndex((h) => /휴대|전화|연락|phone/i.test(h));
  let body = rows.slice(1);
  if (ni < 0) { ni = 0; pi = 1; body = rows; }
  return body.map((r) => ({ name: String(r[ni] || '').trim(), phone: String(r[pi] ?? '').trim() })).filter((r) => r.name);
};

const CoursesTab: React.FC<{ goAttendance: () => void }> = ({ goAttendance }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [showRetired, setShowRetired] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [edit, setEdit] = useState<Partial<Course> | null>(null);
  const [error, setError] = useState('');
  const [roster, setRoster] = useState<RosterTeacher[]>([]);

  const load = () => api<{ data: Course[] }>(`/admin/edu/courses${showRetired ? '?includeRetired=1' : ''}`).then((r) => {
    setCourses(r.data);
    if (!selectedId && r.data.length) setSelectedId(r.data[0]._id);
  }).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [showRetired]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api<{ data: RosterTeacher[] }>('/admin/edu/teachers').then((r) => setRoster(r.data)).catch(() => undefined); }, []);

  const selected = courses.find((c) => c._id === selectedId);
  const categories = Array.from(new Set([...BASE_CATEGORIES, ...courses.map((c) => c.category)]));

  const saveCourse = async () => {
    setError('');
    try {
      if (edit?._id) await api(`/admin/edu/courses/${edit._id}`, { method: 'PUT', body: edit });
      else await api('/admin/edu/courses', { body: edit });
      setEdit(null); load();
    } catch (e: any) { setError(e.message); }
  };
  const removeCourse = async (c: Course) => {
    if (c.recordCount) {
      if (!window.confirm(`이수 기록 ${c.recordCount}건이 있어 삭제할 수 없습니다. 폐지할까요? (목록에서 숨기고 기록은 남깁니다)`)) return;
      await api(`/admin/edu/courses/${c._id}/status`, { body: { status: 'retired' } }).catch((e) => setError(e.message));
    } else {
      if (!window.confirm(`${c.code} 과정을 삭제할까요?${c.sessionCount ? ` 개설된 강의 ${c.sessionCount}개도 함께 지워집니다.` : ''}`)) return;
      await api(`/admin/edu/courses/${c._id}`, { method: 'DELETE' }).catch((e) => setError(e.message));
    }
    setSelectedId(''); load();
  };
  const restore = async (c: Course) => { await api(`/admin/edu/courses/${c._id}/status`, { body: { status: 'active' } }); load(); };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px 1fr' }, gap: 2, alignItems: 'start' }}>
      <Card title="교육 과정" action={<Button variant="contained" size="small" onClick={() => setEdit({ mode: 'session', cycleYears: 1, category: '심화', prerequisites: [], issuesCertificate: false })}>＋ 과정 추가</Button>}>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
        {categories.map((cat) => {
          const items = courses.filter((c) => c.category === cat);
          if (!items.length) return null;
          return (
            <Box key={cat} sx={{ mb: 1.5 }}>
              <Typography fontSize={12} fontWeight={800} color={C.sub} mb={0.5}>{cat === 'CC' ? 'CC · Command College (조정관 학교)' : cat}</Typography>
              {items.map((c) => (
                <Box key={c._id} onClick={() => setSelectedId(c._id)}
                  sx={{ p: 1, borderRadius: 1.5, cursor: 'pointer', mb: 0.5, border: `1px solid ${selectedId === c._id ? '#f0cf5a' : 'transparent'}`, bgcolor: selectedId === c._id ? '#fff6cc' : undefined, opacity: c.status === 'retired' ? 0.55 : 1, '&:hover': { bgcolor: '#fffbea' } }}>
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                    <Typography fontWeight={800} fontSize={14}>{c.code}</Typography>
                    {c.mode === 'yearly' && <ToneChip tone="info" label={c.cycleYears === 2 ? '2년마다' : '매년'} />}
                    {c.status === 'retired' && <ToneChip tone="gray" label="폐지" />}
                    <Box flex={1} />
                    <Typography fontSize={12} color={C.sub}>{c.recordCount}명</Typography>
                  </Box>
                  <Typography fontSize={12.5} color={C.sub}>{c.name}</Typography>
                </Box>
              ))}
            </Box>
          );
        })}
        <FormControlLabel control={<Switch size="small" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />} label={<Typography fontSize={13}>폐지된 과정 보기</Typography>} />
      </Card>

      {selected ? (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Card title={<>{selected.code} · {selected.name}</>} action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" startIcon={<Edit />} onClick={() => setEdit(selected)}>수정</Button>
              {selected.status === 'retired'
                ? <Button size="small" onClick={() => restore(selected)}>다시 운영</Button>
                : !selected.system && <Button size="small" color="error" onClick={() => removeCourse(selected)}>{selected.recordCount ? '폐지' : '삭제'}</Button>}
            </Box>
          }>
            <Typography fontSize={13.5}>{selected.description}</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1, fontSize: 13, color: C.sub }}>
              <span>운영: {selected.mode === 'yearly' ? `연도별 수료 체크 (${selected.cycleYears === 2 ? '2년마다' : '매년'})` : '강의 개설형'}</span>
              {selected.criteria && <span>이수 기준: {selected.criteria}</span>}
              {selected.prerequisites.length > 0 && <span>선수과목: {selected.prerequisites.join(', ')}</span>}
              {selected.issuesCertificate && <span>수료증 발급</span>}
            </Box>
          </Card>
          {selected.code === 'BT'
            ? <Alert severity="info">BT 수료는 ‘완성보고서 승인’에서 처리되며, 승인하면 교사 교육 이력에 자동으로 기록됩니다. BT 강의 출석은 ‘출석 코드’ 탭에서 관리합니다.</Alert>
            : selected.mode === 'yearly'
              ? <YearlyPanel course={selected} roster={roster} courses={courses} onChanged={load} />
              : <SessionPanel course={selected} roster={roster} onChanged={load} goAttendance={goAttendance} />}
        </Box>
      ) : <Empty>과정을 선택하세요.</Empty>}

      <Dialog open={Boolean(edit)} onClose={() => setEdit(null)} fullWidth maxWidth="sm">
        <DialogTitle fontWeight={800}>{edit?._id ? '과정 수정' : '과정 추가'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '8px !important' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 1.5 }}>
            <TextField label="코드" value={edit?.code || ''} disabled={edit?.system} onChange={(e) => setEdit({ ...edit, code: e.target.value.toUpperCase() })} placeholder="ADV101" />
            <TextField label="과정명" value={edit?.name || ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
          </Box>
          <Autocomplete freeSolo options={categories} value={edit?.category || ''} onInputChange={(_, v) => setEdit({ ...edit, category: v })}
            renderInput={(p) => <TextField {...p} label="분류 (기본 / 심화 / CC / 특강 / 기타 또는 직접 입력)" />} />
          <TextField label="설명" value={edit?.description || ''} multiline onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
          <TextField label="이수 기준" value={edit?.criteria || ''} onChange={(e) => setEdit({ ...edit, criteria: e.target.value })} placeholder="예) 4개 강의 중 3개 이상 출석" />
          <Autocomplete multiple options={courses.filter((c) => c._id !== edit?._id).map((c) => c.code)} value={edit?.prerequisites || []}
            onChange={(_, v) => setEdit({ ...edit, prerequisites: v })} renderInput={(p) => <TextField {...p} label="선수과목" />} />
          <TextField select label="운영 방식" value={edit?.mode || 'session'} disabled={edit?.system} onChange={(e) => setEdit({ ...edit, mode: e.target.value as any })}>
            <MenuItem value="session">강의 개설형 — 강의를 열고 출석·이수 등록</MenuItem>
            <MenuItem value="yearly">연도별 수료 체크형 — 강의 개설 없이 몇 년도에 수료했는지만</MenuItem>
          </TextField>
          {edit?.mode === 'yearly' && (
            <TextField select label="개설 주기" value={edit?.cycleYears || 1} onChange={(e) => setEdit({ ...edit, cycleYears: Number(e.target.value) as 1 | 2 })}>
              <MenuItem value={1}>매년</MenuItem><MenuItem value={2}>2년마다</MenuItem>
            </TextField>
          )}
          <FormControlLabel control={<Checkbox checked={Boolean(edit?.issuesCertificate)} onChange={(e) => setEdit({ ...edit, issuesCertificate: e.target.checked })} />} label="수료증 발급 과정" />
        </DialogContent>
        <DialogActions><Button onClick={() => setEdit(null)}>닫기</Button><Button variant="contained" disabled={!edit?.code || !edit?.name} onClick={saveCourse}>저장</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

/** 교사 선택 (검색 + 체크) */
const TeacherPicker: React.FC<{ roster: RosterTeacher[]; value: string[]; onChange: (ids: string[]) => void; warn?: (t: RosterTeacher) => string }> = ({ roster, value, onChange, warn }) => {
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const kw = q.trim();
    const base = kw ? roster.filter((t) => [t.teacherName, t.churchName, t.regNo, t.teacherPhone].some((v) => String(v).includes(kw))) : roster;
    return [...base].sort((a, b) => Number(value.includes(b._id)) - Number(value.includes(a._id))).slice(0, 300);
  }, [roster, q, value]);
  return (
    <Box>
      <TextField size="small" fullWidth placeholder="이름 · 교회 · 등록번호 · 휴대폰" value={q} onChange={(e) => setQ(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      <Typography fontSize={12} color={C.sub} my={0.5}>선택 {value.length}명</Typography>
      <Box sx={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${C.line}`, borderRadius: 1.5 }}>
        {list.map((t) => {
          const w = warn?.(t);
          return (
            <Box key={t._id} sx={{ display: 'flex', alignItems: 'center', px: 1, borderBottom: `1px solid ${C.line}` }}>
              <Checkbox size="small" checked={value.includes(t._id)} onChange={(e) => onChange(e.target.checked ? [...value, t._id] : value.filter((x) => x !== t._id))} />
              <Typography fontSize={13.5} fontWeight={700} sx={{ minWidth: 70 }}>{t.teacherName}</Typography>
              <Typography fontSize={12.5} color={C.sub} sx={{ flex: 1 }}>{t.churchName} · {t.teacherPhone}</Typography>
              {w && <Typography fontSize={11.5} color={C.warn}>⚠ {w}</Typography>}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const YearlyPanel: React.FC<{ course: Course; roster: RosterTeacher[]; courses: Course[]; onChanged: () => void }> = ({ course, roster, courses, onChanged }) => {
  const [checkYear, setCheckYear] = useState<number | null>(null);
  const [ids, setIds] = useState<string[]>([]);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const years = Array.from(new Set([...course.years, ...course.yearCounts.map((y) => y.year)])).sort((a, b) => b - a);
  const nextYear = (years[0] || new Date().getFullYear() - course.cycleYears) + course.cycleYears;

  const openCheck = async (year: number) => {
    const r = await api<{ data: Array<{ personKey: string }> }>(`/admin/edu/courses/${course._id}/years/${year}/records`);
    const keys = new Set(r.data.map((x) => x.personKey));
    setIds(roster.filter((t) => keys.has(t.personKey)).map((t) => t._id));
    setCheckYear(year); setMsg(''); setError('');
  };
  const save = async () => {
    try {
      const r = await api<{ data: { count: number; removed: number } }>(`/admin/edu/courses/${course._id}/years/${checkYear}/records`, { method: 'PUT', body: { teacherIds: ids } });
      setMsg(`${checkYear}년 수료 ${r.data.count}명 저장${r.data.removed ? ` (해제 ${r.data.removed}명)` : ''}`);
      setCheckYear(null); onChanged();
    } catch (e: any) { setError(e.message); }
  };
  const importExcel = async (file?: File) => {
    if (!file) return;
    const rows = await readExcelRows(file);
    const digits = (v: string) => v.replace(/\D/g, '');
    const found = roster.filter((t) => rows.some((r) => r.name === t.teacherName && (!digits(r.phone) || digits(r.phone) === digits(t.teacherPhone))));
    setIds(Array.from(new Set([...ids, ...found.map((t) => t._id)])));
    setError(found.length < rows.length ? `엑셀 ${rows.length}명 중 ${found.length}명을 찾았습니다. 못 찾은 사람은 이름·휴대폰을 확인해주세요.` : '');
  };
  const addYear = async () => { await api(`/admin/edu/courses/${course._id}/years`, { body: { year: nextYear } }); onChanged(); };
  const removeYear = async (y: number) => { await api(`/admin/edu/courses/${course._id}/years/${y}`, { method: 'DELETE' }).then(onChanged).catch((e) => setError(e.message)); };
  const prereqWarn = (t: RosterTeacher) => {
    const missing = course.prerequisites.filter((p) => !t.education.some((e) => e.code === p && e.result === 'completed'));
    return missing.length ? `${missing.join(', ')} 미수료` : '';
  };

  return (
    <Card title="연도별 수료" action={<Button size="small" variant="outlined" onClick={addYear}>＋ {nextYear}년 추가</Button>}>
      {msg && <Alert severity="success" sx={{ mb: 1 }}>{msg}</Alert>}
      {error && !checkYear && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Typography fontSize={13} color={C.sub} mb={1}>{course.cycleYears === 2 ? '2년마다' : '매년'} 개설 · 과정 안의 여러 강의는 따로 개설하지 않고, 그 해 수료자만 체크합니다.</Typography>
      {!years.length && <Empty>운영 연도를 추가하세요.</Empty>}
      {years.map((y) => {
        const n = course.yearCounts.find((x) => x.year === y)?.count || 0;
        return (
          <Box key={y} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: `1px solid ${C.line}` }}>
            <Typography fontWeight={800} sx={{ width: 70 }}>{y}년</Typography>
            <Typography sx={{ flex: 1 }} fontSize={14}>{n ? <><b>{n}명</b> 수료</> : <span style={{ color: C.sub }}>수료자 없음</span>}</Typography>
            <Button size="small" variant="contained" onClick={() => openCheck(y)}>수료 체크</Button>
            {!n && <IconButton size="small" onClick={() => removeYear(y)}><Delete fontSize="small" /></IconButton>}
          </Box>
        );
      })}
      <Dialog open={checkYear !== null} onClose={() => setCheckYear(null)} fullWidth maxWidth="sm">
        <DialogTitle fontWeight={800}>{course.code} · {checkYear}년 수료 체크</DialogTitle>
        <DialogContent>
          {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
          {course.prerequisites.length > 0 && <Alert severity="info" sx={{ mb: 1 }}>선수과목 {course.prerequisites.join(', ')} — 미수료자는 ⚠ 표시 (체크는 가능)</Alert>}
          <Button component="label" size="small" variant="outlined" sx={{ mb: 1 }}>엑셀로 불러오기 (이름·휴대폰)<input hidden type="file" accept=".xlsx,.xls,.csv" onChange={(e) => importExcel(e.target.files?.[0])} /></Button>
          <TeacherPicker roster={roster} value={ids} onChange={setIds} warn={course.prerequisites.length ? prereqWarn : undefined} />
          <Typography fontSize={11.5} color={C.sub} mt={1}>체크를 해제하고 저장하면 그 해 수료 기록이 지워집니다. 과정 목록: {courses.length}개</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setCheckYear(null)}>닫기</Button><Button variant="contained" onClick={save}>{ids.length}명 저장</Button></DialogActions>
      </Dialog>
    </Card>
  );
};

const SessionPanel: React.FC<{ course: Course; roster: RosterTeacher[]; onChanged: () => void; goAttendance: () => void }> = ({ course, roster, onChanged, goAttendance }) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [edit, setEdit] = useState<any>(null);
  const [active, setActive] = useState<Session | null>(null);
  const [records, setRecords] = useState<Rec[]>([]);
  const [pick, setPick] = useState<string[]>([]);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = () => api<{ data: Session[] }>(`/admin/edu/courses/${course._id}/sessions`).then((r) => setSessions(r.data)).catch((e) => setError(e.message));
  useEffect(() => { load(); setActive(null); }, [course._id]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadRecords = (s: Session) => api<{ data: Rec[] }>(`/admin/edu/sessions/${s._id}/records`).then((r) => setRecords(r.data));
  useEffect(() => { if (active) loadRecords(active); setPick([]); setMsg(''); }, [active]);

  const saveSession = async () => {
    try {
      if (edit._id) await api(`/admin/edu/sessions/${edit._id}`, { method: 'PUT', body: edit });
      else await api(`/admin/edu/courses/${course._id}/sessions`, { body: edit });
      setEdit(null); load(); onChanged();
    } catch (e: any) { setError(e.message); }
  };
  const removeSession = async (s: Session) => {
    if (!window.confirm(`'${s.title}' 강의를 삭제할까요?${s.recordCount ? ` 출석·이수 기록 ${s.recordCount}건도 함께 지워집니다.` : ''}`)) return;
    await api(`/admin/edu/sessions/${s._id}`, { method: 'DELETE' });
    if (active?._id === s._id) setActive(null);
    load(); onChanged();
  };
  const addRecords = async (body: any) => {
    const r = await api<{ data: { added: number; notFound: Array<{ name: string; phone: string }> } }>(`/admin/edu/sessions/${active!._id}/records`, { body });
    setMsg(`${r.data.added}명 등록${r.data.notFound.length ? ` · 못 찾음 ${r.data.notFound.length}명: ${r.data.notFound.slice(0, 10).map((x) => x.name).join(', ')}` : ''}`);
    setPick([]); loadRecords(active!); load(); onChanged();
  };
  const removeRecord = async (id: string) => { await api(`/admin/edu/records/${id}`, { method: 'DELETE' }); loadRecords(active!); load(); onChanged(); };

  return (
    <Card title="개설된 강의" action={<Button size="small" variant="contained" onClick={() => setEdit({ title: '', date: '', place: '', recruiting: true })}>＋ 강의 개설</Button>}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {!sessions.length && <Empty>개설된 강의가 없습니다.</Empty>}
      {sessions.map((s) => (
        <Box key={s._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1, borderBottom: `1px solid ${C.line}`, bgcolor: active?._id === s._id ? '#fff6cc' : undefined, cursor: 'pointer' }} onClick={() => setActive(s)}>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={800} fontSize={14}>{s.title} {s.recruiting && <Chip size="small" label="모집 중" sx={{ height: 20, ml: 0.5, bgcolor: '#fff3cf', color: '#8a5d00' }} />}</Typography>
            <Typography fontSize={12.5} color={C.sub}>{s.date ? ymd(s.date) : '날짜 미정'} {s.place && `· ${s.place}`} · 이수 {s.recordCount}명</Typography>
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEdit({ ...s, date: s.date ? ymd(s.date) : '' }); }}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeSession(s); }}><Delete fontSize="small" /></IconButton>
        </Box>
      ))}

      {active && (
        <Box sx={{ mt: 2, p: 1.5, border: `1px solid #f0d77a`, borderRadius: 2, bgcolor: '#fffdf4' }}>
          <Typography fontWeight={800} mb={1}>‘{active.title}’ 출석·이수 등록</Typography>
          {msg && <Alert severity="success" sx={{ mb: 1 }}>{msg}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Button component="label" size="small" variant="outlined">엑셀 (이름·휴대폰)<input hidden type="file" accept=".xlsx,.xls,.csv"
              onChange={async (e) => { const f = e.target.files?.[0]; if (f) addRecords({ rows: await readExcelRows(f), source: 'excel' }); e.target.value = ''; }} /></Button>
            <Button size="small" variant="outlined" onClick={goAttendance}>QR 출석 코드로 받기</Button>
          </Box>
          <TeacherPicker roster={roster} value={pick} onChange={setPick} />
          <Button sx={{ mt: 1 }} variant="contained" size="small" disabled={!pick.length} onClick={() => addRecords({ teacherIds: pick })}>선택 {pick.length}명 이수 등록</Button>
          <Typography fontWeight={800} mt={2} mb={0.5} fontSize={14}>등록된 {records.length}명</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {records.map((r) => <Chip key={r._id} size="small" label={`${r.teacherName}${r.source !== 'manual' ? ` · ${r.source}` : ''}`} onDelete={() => removeRecord(r._id)} />)}
          </Box>
        </Box>
      )}

      <Dialog open={Boolean(edit)} onClose={() => setEdit(null)} fullWidth maxWidth="xs">
        <DialogTitle fontWeight={800}>{edit?._id ? '강의 수정' : `${course.code} 강의 개설`}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '8px !important' }}>
          <TextField label="강의 제목" value={edit?.title || ''} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
          <TextField type="date" label="날짜" InputLabelProps={{ shrink: true }} value={edit?.date || ''} onChange={(e) => setEdit({ ...edit, date: e.target.value })} />
          <TextField label="장소" value={edit?.place || ''} onChange={(e) => setEdit({ ...edit, place: e.target.value })} />
          <FormControlLabel control={<Switch checked={Boolean(edit?.recruiting)} onChange={(e) => setEdit({ ...edit, recruiting: e.target.checked })} />} label="모집 중" />
        </DialogContent>
        <DialogActions><Button onClick={() => setEdit(null)}>닫기</Button><Button variant="contained" disabled={!edit?.title} onClick={saveSession}>저장</Button></DialogActions>
      </Dialog>
    </Card>
  );
};

export default CoursesTab;
